import { CREDITS_PER_USD, normalizeCallKind } from '../constant';

/** Minimum call length (seconds) before subscriber wallet deduct runs (same threshold as creator summary earnings). */
export const MIN_BILLED_CALL_DURATION_SEC = 5;

// --- Post-call duration (timer refs + UI mirror) — single source for deduct + summary ---

/**
 * Connected seconds from duration timer refs: accumulated plus the running segment since `startAt` was set.
 * Matches {@link useCallDurationTimer} / CallingScreen pause semantics; used when freezing duration at hangup.
 *
 * @param {{ current?: number } | null | undefined} accumulatedSecRef
 * @param {{ current?: number | null } | null | undefined} durationStartAtRef — `Date.now()` when the running segment started, or unset when paused
 * @returns {number}
 */
export const getFrozenConnectedDurationSecFromRefs = (accumulatedSecRef, durationStartAtRef) => {
  const accumulatedSec = accumulatedSecRef?.current || 0;
  const startedAt = durationStartAtRef?.current;
  const runningExtraSec =
    typeof startedAt === 'number' ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000)) : 0;
  return Math.max(0, accumulatedSec + runningExtraSec);
};

/**
 * Prefer the larger of frozen timer duration and UI-displayed duration so billing matches what the user saw
 * if refs lag during teardown.
 *
 * @param {number} frozenSec
 * @param {unknown} latestDisplayedSec — e.g. ref.current from the in-call duration label
 * @returns {number} non-negative integer seconds
 */
export const mergeFrozenAndDisplayedCallDurationSec = (frozenSec, latestDisplayedSec) => {
  const fromUi = Number(latestDisplayedSec);
  const merged =
    Number.isFinite(fromUi) && fromUi > 0 ? Math.max(frozenSec, fromUi) : frozenSec;
  return Math.max(0, Math.floor(merged));
};

/** Max seconds past a flat-rate block boundary to treat as timer skew (not extra billable time). */
export const POST_CALL_FLAT_RATE_BOUNDARY_SKEW_SEC = 5;

/**
 * Flat-rate calls that end for insufficient credits are hung up at the next block boundary.
 * The creator client timer can read a few seconds past that boundary; snap back so summary
 * and deduct match the subscriber.
 *
 * @param {number} durationSec
 * @param {number} blockMinutes
 * @returns {number}
 */
export const normalizeFlatRateInsufficientCreditsBillingDuration = (
  durationSec,
  blockMinutes
) => {
  const blockSec = Math.max(60, Math.round(Number(blockMinutes) || 0) * 60);
  const elapsed = Math.max(0, Math.floor(Number(durationSec) || 0));
  if (elapsed < blockSec) return elapsed;

  const blocks = getFlatRateBlocksCharged(elapsed, blockMinutes);
  const boundarySec = Math.max(0, (blocks - 1) * blockSec);
  if (elapsed <= boundarySec) return elapsed;

  const overshoot = elapsed - boundarySec;
  if (overshoot <= POST_CALL_FLAT_RATE_BOUNDARY_SKEW_SEC) {
    return boundarySec;
  }
  return elapsed;
};

/**
 * @param {number} durationSec
 * @param {{ creatorInfo?: object|null, callKind?: 'voice'|'video', endedReason?: string|null }} [billingContext]
 * @returns {number}
 */
export const applyPostCallBillingDurationAdjustments = (durationSec, billingContext) => {
  const creatorInfo = billingContext?.creatorInfo;
  const callKind = billingContext?.callKind;
  const endedReason = billingContext?.endedReason;
  if (!creatorInfo || !callKind || !isInsufficientCreditsTermination(endedReason)) {
    return durationSec;
  }

  const pricing = getCallRequestPricingDetails(creatorInfo, callKind);
  if (!pricing.ok || pricing.pricingModel !== 'flat_rate') {
    return durationSec;
  }

  return normalizeFlatRateInsufficientCreditsBillingDuration(
    durationSec,
    pricing.minDurationMins
  );
};

export const resolvePostCallBillingDurationSec = (
  durationAccumulatedSecRef,
  durationStartAtRef,
  latestDisplayedCallDurationSecRef,
  billingContext = null
) => {
  const frozen = getFrozenConnectedDurationSecFromRefs(
    durationAccumulatedSecRef,
    durationStartAtRef
  );
  const merged = mergeFrozenAndDisplayedCallDurationSec(
    frozen,
    latestDisplayedCallDurationSecRef?.current
  );
  return applyPostCallBillingDurationAdjustments(merged, billingContext);
};

/**
 * Two decimal places using the thousandths digit: if it is 5–9, round the hundredths up; otherwise leave hundredths as-is.
 * Uses `round(abs × 1000)` so values like 3.335 (from 6.67 / 2) stay stable in float.
 * @param {number} n
 */
const roundToTwoByThousandthsDigit = (n) => {
  if (!Number.isFinite(n)) return 0;
  const sign = n < 0 ? -1 : 1;
  const x = Math.abs(n);
  const thousandths = Math.round(x * 1000);
  const firstTwoDecimals = Math.floor(thousandths / 10);
  const thirdDigit = thousandths % 10;
  const hundredths = firstTwoDecimals + (thirdDigit >= 5 ? 1 : 0);
  return sign * (hundredths / 100);
};

const roundCredits = (n) => roundToTwoByThousandthsDigit(n);

/** `(minutes × 60 + seconds) × (rate / 60)` using final 2-decimal rounding. */
export const calculateCreatorEarningsUsdPerSecond = (durationSec, ratePerMinuteUsd) => {
  const sec = Math.max(0, Number(durationSec) || 0);
  const rate = Number(ratePerMinuteUsd) || 0;
  return roundToTwoByThousandthsDigit(sec * (rate / 60));
};

/**
 * Converts a credit amount to USD with the same 2-decimal rule as credits (e.g. 6.67 credits → $3.34/min).
 * @param {number} credits
 */
export const creditsToUsdRounded = (credits) =>
  roundToTwoByThousandthsDigit(Number(credits) / CREDITS_PER_USD);

const PRICING_INVALID = "Call pricing is incomplete or invalid.";

/** @param {string|null|undefined} name */
export const formatHandle = (name) => {
  if (name == null || name === "") return "@unknown";
  const s = String(name).trim();
  if (!s || s.toLowerCase() === "unknown") return "@unknown";
  return s.startsWith("@") ? s : `@${s}`;
};

export const formatCallDuration = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

/** Human-readable label for matrix-js-sdk voice/video call `state` strings shown in the in-call header. */
export const getMatrixSdkCallStateLabel = (sdkCallState) => {
  switch (sdkCallState) {
    case "ringing":
      return "Ringing";
    case "connecting":
      return "Connecting…";
    case "connected":
      return "Connected";
    case "invite_sent":
      return "Waiting for answer…";
    case "wait_local_media":
      return "Preparing media…";
    case "ended":
      return "Ended";
    default:
      return sdkCallState ? String(sdkCallState) : null;
  }
};

/**
 * Banner text when the browser or ICE layer is recovering (voice uses softer copy on failed ICE while connected).
 * @param {{ isOnline?: boolean, iceConnectionState?: string|null, sdkCallState?: string|null, variant?: 'voice' | 'video' }} p
 */
export const getCallReconnectBannerLabel = ({
  isOnline,
  iceConnectionState,
  sdkCallState,
  variant = "video",
}) => {
  if (isOnline === false) return "Network lost, reconnecting…";
  if (iceConnectionState === "disconnected") return "Reconnecting…";
  if (iceConnectionState === "failed") {
    if (variant === "voice") {
      return sdkCallState === "connected" ? "Connection unstable, reconnecting…" : "Connecting…";
    }
    return "Connection failed";
  }
  return null;
};

export const formatCreditsValue = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "0";

  const options = Number.isInteger(num)
    ? { maximumFractionDigits: 0 }
    : { minimumFractionDigits: 2, maximumFractionDigits: 2 };

  return new Intl.NumberFormat(undefined, options).format(num);
};

/**
 * Resolve creator in-call USD/min from either an override offer or creator profile settings.
 */
export const getCreatorRatePerMinuteUsd = ({
  isCreator,
  isVoiceCallUi,
  currentUser,
  callRateOverride,
}) => {
  if (!isCreator) return 0;
  if (callRateOverride?.pricingModel === 'per_minute') {
    return Number(callRateOverride.perMinuteAmount) || 0;
  }
  if (callRateOverride?.pricingModel === 'flat_rate') {
    const totalUsd = Number(callRateOverride.flatRateAmount) || 0;
    const blockMins = Number(callRateOverride.flatRateDurationMinutes) || 0;
    return blockMins > 0 ? totalUsd / blockMins : 0;
  }
  const callSettings = isVoiceCallUi ? currentUser?.voice : currentUser?.video;
  const pricingModel = callSettings?.pricingModel;
  if (pricingModel === 'per_minute') {
    return Number(callSettings?.perMinute?.amount) || 0;
  }
  if (pricingModel === 'flat_rate') {
    const totalUsd = Number(callSettings?.flatRate?.amount) || 0;
    const blockMins = Number(callSettings?.flatRate?.durationMinutes) || 0;
    return blockMins > 0 ? totalUsd / blockMins : 0;
  }
  return 0;
};

/**
 * Voice/video call row for the request modal (matches creator profile call pricing shape).
 * @param {object|null|undefined} creatorInfo
 * @param {'voice'|'video'} callKind
 * Credit amounts use thousandths-based 2-decimal rounding (5–9 in the third decimal rounds the second up).
 * Creator USD/min from billed credits: {@link creditsToUsdRounded}`(ratePerMinuteCredits)`.
 * @returns {{ ok: true, pricingModel: string, minDurationMins: number, minCostCredits: number, sessionCostCredits?: number, flatRateCredits?: number, ratePerMinuteCredits: number, ratePerMinuteRow: string } | { ok: false, error: string }}
 */
export const getCallRequestPricingDetails = (creatorInfo, callKind) => {
  const isVideo = normalizeCallKind(callKind) === "video";
  const settings = isVideo ? creatorInfo?.video : creatorInfo?.voice;

  if (settings == null || typeof settings !== "object") {
    return {
      ok: false,
      error: isVideo
        ? "Video call pricing is not set up for this creator."
        : "Voice call pricing is not set up for this creator.",
    };
  }

  if (settings.pricingModel === "per_minute") {
    const pm = settings.perMinute || {};
    const usdPerMinute = Number(pm.amount);
    const rawMinDuration = Number(pm.minCallMinutess);
    const hasMinimumDuration = Number.isFinite(rawMinDuration) && rawMinDuration >= 1;
    const minDurationMins = hasMinimumDuration ? Math.round(rawMinDuration) : 0;
    if (!Number.isFinite(usdPerMinute) || usdPerMinute <= 0) {
      return { ok: false, error: PRICING_INVALID };
    }
    // (USD/min ÷ 1) × CREDITS_PER_USD — e.g. $4/min → 8 credits/min
    const ratePerMinuteCredits = roundCredits(usdPerMinute * CREDITS_PER_USD);
    const minCostCredits = hasMinimumDuration
      ? roundCredits(ratePerMinuteCredits * minDurationMins)
      : roundCredits(Math.max(1, ratePerMinuteCredits));
    return {
      ok: true,
      pricingModel: "per_minute",
      minDurationMins,
      hasMinimumDuration,
      amountUsd: usdPerMinute,
      usdPerMinute,
      minCostCredits,
      ratePerMinuteCredits,
      ratePerMinuteRow: `${formatCreditsValue(ratePerMinuteCredits)} credits`,
    };
  }

  if (settings.pricingModel === "flat_rate") {
    const fr = settings.flatRate || {};
    const flatRateTotalUsd = Number(fr.amount);
    const minDurationMins = Math.round(Number(fr.durationMinutes));
    if (
      !Number.isFinite(flatRateTotalUsd) ||
      flatRateTotalUsd <= 0 ||
      !Number.isFinite(minDurationMins) ||
      minDurationMins < 1
    ) {
      return { ok: false, error: PRICING_INVALID };
    }
    // Derived USD/min: flat session price ÷ block minutes (e.g. $50 / 5 min → $10/min → 20 credits/min).
    const usdPerMinute = roundToTwoByThousandthsDigit(flatRateTotalUsd / minDurationMins);
    const ratePerMinuteCredits = roundCredits(usdPerMinute * CREDITS_PER_USD);
    const sessionCostCredits = roundCredits(ratePerMinuteCredits * minDurationMins);
    const flatRateCredits = roundCredits(flatRateTotalUsd * CREDITS_PER_USD);
    return {
      ok: true,
      pricingModel: "flat_rate",
      minDurationMins,
      hasMinimumDuration: true,
      amountUsd: roundToTwoByThousandthsDigit(flatRateTotalUsd),
      usdPerMinute,
      minCostCredits: flatRateCredits,
      sessionCostCredits,
      flatRateCredits,
      ratePerMinuteCredits,
      ratePerMinuteRow: `${formatCreditsValue(ratePerMinuteCredits)} credits`,
    };
  }

  return { ok: false, error: PRICING_INVALID };
};

/**
 * Subscriber outbound / answer gate: wallet must cover {@link getCallRequestPricingDetails}'s minCostCredits
 * (per-minute: one minute or minimum duration; flat-rate: one full session block upfront).
 * @param {{ ok: true, minCostCredits: number } | { ok: false }} pricing
 * @param {number} credits — parsed wallet credits
 * @param {boolean} walletBalanceLoading
 */
export const subscriberHasMinimumCreditsForCall = (pricing, credits, walletBalanceLoading) => {
  if (walletBalanceLoading) return false;
  if (!pricing || !pricing.ok) return false;
  if (!Number.isFinite(credits)) return false;
  return credits >= pricing.minCostCredits;
};

/** Show low-balance warning (subscriber purchase modal + creator alert) when estimated talk time left is at or under this many minutes. */
export const LOW_BALANCE_WARNING_MINUTES = 5;
/** Second, more urgent low-balance warning tier (e.g. flat-rate next block or per-minute burn). */
export const LOW_BALANCE_CRITICAL_WARNING_MINUTES = 1;

/** Ordered low-balance warning tiers (most urgent last). */
export const LOW_BALANCE_WARNING_TIER_MINUTES = [
  LOW_BALANCE_WARNING_MINUTES,
  LOW_BALANCE_CRITICAL_WARNING_MINUTES,
];

const handledLowBalanceTiersForCall = (handledTierKeys, callId) =>
  LOW_BALANCE_WARNING_TIER_MINUTES.filter((tier) => handledTierKeys.has(`${callId}:${tier}`));

/**
 * Low-balance warning tiers newly crossed for `minutesRemaining` that are not in `handledTierKeys`.
 * @param {number} minutesRemaining
 * @param {string} callId
 * @param {Set<string>} handledTierKeys — `${callId}:${tierMinutes}` entries
 * @param {{ exclusiveUpperBound?: boolean }} [options]
 *   When true, tiers fire only after dropping strictly below the threshold (flat-rate block
 *   start often equals exactly 5 minutes remaining — avoid warning on connect).
 * @returns {number[]} newly crossed tiers, most urgent last
 */
export const getNewLowBalanceWarningTiers = (
  minutesRemaining,
  callId,
  handledTierKeys,
  { exclusiveUpperBound = false } = {}
) => {
  if (!Number.isFinite(minutesRemaining) || !callId) return [];
  const handled = handledLowBalanceTiersForCall(handledTierKeys, callId);
  const crossesTier = exclusiveUpperBound
    ? (tier) => minutesRemaining < tier
    : (tier) => minutesRemaining <= tier;
  return LOW_BALANCE_WARNING_TIER_MINUTES.filter(
    (tier) => crossesTier(tier) && !handled.includes(tier)
  );
};

/**
 * Flat-rate blocks charged so far: one block upfront at connect, then one per completed block period.
 * @param {number} durationSec — connected call duration in seconds
 * @param {number} blockMinutes — flat-rate session length in minutes
 * @returns {number} positive integer block count
 */
export const getFlatRateBlocksCharged = (durationSec, blockMinutes) => {
  const blockSec = Math.max(1, Math.round(Number(blockMinutes) || 0) * 60);
  const elapsedSec = Math.max(0, Math.floor(Number(durationSec) || 0));
  return Math.max(1, 1 + Math.floor(elapsedSec / blockSec));
};

/**
 * Seconds until the next flat-rate block boundary (when the next block would be charged).
 *
 * @param {number} durationSec — connected call duration in seconds
 * @param {number} blockMinutes — flat-rate session length in minutes
 * @returns {number} non-negative integer seconds
 */
export const getSecondsUntilNextFlatRateBlockBoundary = (durationSec, blockMinutes) => {
  const blockSec = Math.max(1, Math.round(Number(blockMinutes) || 0) * 60);
  const elapsedSec = Math.max(0, Math.floor(Number(durationSec) || 0));
  if (elapsedSec === 0) return blockSec;
  const elapsedInCurrentBlock = elapsedSec % blockSec;
  if (elapsedInCurrentBlock === 0) return 0;
  return blockSec - elapsedInCurrentBlock;
};

/**
 * True while the wallet covers all flat-rate blocks already committed for elapsed time
 * (one block upfront at connect, then one per completed block period).
 *
 * @param {number} walletCredits
 * @param {number} durationSec
 * @param {number} blockCredits
 * @param {number} blockMinutes
 */
export const subscriberCanContinueFlatRateCall = (
  walletCredits,
  durationSec,
  blockCredits,
  blockMinutes
) => {
  if (!Number.isFinite(walletCredits) || !Number.isFinite(blockCredits) || blockCredits <= 0) {
    return false;
  }
  const blocksCharged = getFlatRateBlocksCharged(durationSec, blockMinutes);
  return walletCredits >= roundCredits(blocksCharged * blockCredits);
};

/**
 * True when the subscriber wallet can cover another flat-rate block at the next block boundary.
 * E.g. 200 credits, 200/block, 5 min at connect — can finish block 1 but not block 2 (needs 400).
 *
 * @param {number} walletCredits
 * @param {number} durationSec
 * @param {number} blockCredits
 * @param {number} blockMinutes
 */
export const subscriberCanAffordNextFlatRateBlock = (
  walletCredits,
  durationSec,
  blockCredits,
  blockMinutes
) => {
  if (!Number.isFinite(walletCredits) || !Number.isFinite(blockCredits) || blockCredits <= 0) {
    return false;
  }
  const elapsedSec = Math.max(0, Math.floor(Number(durationSec) || 0));
  const secondsUntilBoundary = getSecondsUntilNextFlatRateBlockBoundary(elapsedSec, blockMinutes);
  const durationAtBoundary = elapsedSec + secondsUntilBoundary;
  const blocksRequired = getFlatRateBlocksCharged(durationAtBoundary, blockMinutes);
  return walletCredits >= roundCredits(blocksRequired * blockCredits);
};

/**
 * Estimates subscriber credits remaining during an active call using wallet balance minus
 * credits burned so far.
 *
 * Per-minute: prorated `elapsedSec × (credits/min ÷ 60)` so “minutes left” decreases smoothly.
 * Flat-rate: block billing — one session block charged upfront, then each block period;
 * remaining time includes the current block plus any additional blocks the wallet can cover.
 *
 * Wallet often does not decrement until post-call deduct; usage is modeled from elapsed time only.
 *
 * @param {object|null|undefined} creatorInfo
 * @param {'voice'|'video'} callKind
 * @param {number} walletCredits — current wallet credits (subscriber)
 * @param {number} durationSec — connected call duration in seconds
 * @returns {{ ok: false } | { ok: true, pricingModel: string, ratePerMinuteCredits: number, blockCredits?: number, remainingCredits: number, minutesRemaining: number, usedCredits: number }}
 */
export const estimateSubscriberCreditsRemainingForActiveCall = (
  creatorInfo,
  callKind,
  walletCredits,
  durationSec
) => {
  const pricing = getCallRequestPricingDetails(creatorInfo, callKind);
  if (!pricing.ok || !Number.isFinite(walletCredits)) {
    return { ok: false };
  }
  const rate = pricing.ratePerMinuteCredits;
  if (!Number.isFinite(rate) || rate <= 0) {
    return { ok: false };
  }

  const elapsedSec = Math.max(0, Math.floor(Number(durationSec) || 0));

  if (pricing.pricingModel === 'flat_rate') {
    const blockMinutes = pricing.minDurationMins;
    const blockCredits =
      pricing.flatRateCredits ??
      pricing.sessionCostCredits ??
      roundCredits(rate * blockMinutes);
    if (!Number.isFinite(blockCredits) || blockCredits <= 0) {
      return { ok: false };
    }

    const blocksCharged = getFlatRateBlocksCharged(elapsedSec, blockMinutes);
    const usedCredits = roundCredits(blocksCharged * blockCredits);
    const remainingCredits = walletCredits - usedCredits;
    const canContinueCall = subscriberCanContinueFlatRateCall(
      walletCredits,
      elapsedSec,
      blockCredits,
      blockMinutes
    );
    const canAffordNextBlock = subscriberCanAffordNextFlatRateBlock(
      walletCredits,
      elapsedSec,
      blockCredits,
      blockMinutes
    );
    const affordableBlocks = canAffordNextBlock
      ? Math.max(0, Math.floor(remainingCredits / blockCredits))
      : 0;
    const secondsUntilBlockBoundary = getSecondsUntilNextFlatRateBlockBoundary(
      elapsedSec,
      blockMinutes
    );
    const secondsRemaining = canAffordNextBlock
      ? secondsUntilBlockBoundary + affordableBlocks * blockMinutes * 60
      : secondsUntilBlockBoundary;
    const minutesRemaining = secondsRemaining / 60;

    return {
      ok: true,
      pricingModel: 'flat_rate',
      ratePerMinuteCredits: rate,
      blockCredits,
      remainingCredits,
      canContinueCall,
      canAffordNextBlock,
      minutesRemaining,
      usedCredits,
    };
  }

  const usedCredits = roundCredits(elapsedSec * (rate / 60));
  const remainingCredits = walletCredits - usedCredits;
  const minutesRemaining = remainingCredits / rate;

  return {
    ok: true,
    pricingModel: 'per_minute',
    ratePerMinuteCredits: rate,
    remainingCredits,
    minutesRemaining,
    usedCredits,
  };
};

/**
 * True while wallet balance has not been fetched yet (e.g. hard refresh on /call where MessageHeader did not load credits).
 * Not pending after a failed fetch (`walletBalanceError` set) so the UI can show an error instead of spinning forever.
 *
 * @param {object | null | undefined} walletBalance
 * @param {boolean} walletBalanceLoading
 * @param {unknown} walletBalanceError
 * @returns {boolean}
 */
export const isWalletBalancePending = (walletBalance, walletBalanceLoading, walletBalanceError) => {
  if (walletBalanceLoading) return true;
  if (walletBalanceError) return false;
  return walletBalance == null;
};

/**
 * True when a call ended because of network / system issues rather than a user
 * explicitly pressing "end". For these cases we bill based on actual minutes
 * (prorated) instead of applying subscriber "full session block" rules.
 *
 * @param {string|null|undefined} endedReason
 */
const isNetworkOrSystemTermination = (endedReason) => {
  if (!endedReason) return false;
  const code = String(endedReason).toLowerCase();
  return (
    code === 'ice_failed' ||
    code === 'icefailed' ||
    code === 'signalling_failed' ||
    code === 'signallingfailed' ||
    // matrix-js-sdk CallErrorCode.SignallingFailed
    code === 'signalling_timeout' ||
    code === 'invite_timeout' ||
    code === 'invitetimeout' ||
    code === 'no_user_media' ||
    code === 'nousermedia' ||
    code === 'timeout' ||
    code === 'network_error' ||
    code === 'networkerror'
  );
};

/**
 * Subscriber ran out of credits mid-call (auto-hangup). Bill prorated seconds only,
 * not a full flat-rate block.
 *
 * @param {string|null|undefined} endedReason
 */
const isInsufficientCreditsTermination = (endedReason) => {
  if (!endedReason) return false;
  return String(endedReason).toLowerCase() === 'insufficient_credits';
};

/**
 * Charge only for connected time (per-minute rate), not flat-rate session blocks.
 *
 * @param {string|null|undefined} endedReason
 */
const isActualTimeOnlyBillingTermination = (endedReason) =>
  isNetworkOrSystemTermination(endedReason) || isInsufficientCreditsTermination(endedReason);

/**
 * True when the remote party (creator) ended the call on this client.
 *
 * Billing treats creator-initiated endings as the "creator hangup" pricing branch
 * even if the termination wasn't a normal `user_hangup` (e.g. ICE/timeout).
 *
 * @param {string|null|undefined} hangupParty
 * @param {string|null|undefined} endedReason
 */
const isRemoteCreatorTermination = (hangupParty, endedReason) =>
  hangupParty === 'remote' && endedReason != null && endedReason !== '';

/**
 * Hangup party is from this Matrix client's POV. {@link buildDeductCallCreditPayload} applies subscriber-side
 * rules, so invert local/remote when the logged-in user is the creator.
 *
 * @param {boolean} isCreatorClient
 * @param {string|null|undefined} hangupParty
 * @returns {string|null|undefined}
 */
export const hangupPartyForCallBilling = (isCreatorClient, hangupParty) => {
  if (!isCreatorClient) return hangupParty ?? null;
  if (hangupParty === 'local') return 'remote';
  if (hangupParty === 'remote') return 'local';
  return hangupParty ?? null;
};

/**
 * Subscriber-side credits to charge for a completed call (same rules as API contract / product spec).
 *
 * @param {{ ok: true, pricingModel: string, minDurationMins: number, minCostCredits: number, ratePerMinuteCredits: number }} pricing — from {@link getCallRequestPricingDetails}
 * @param {number} durationSec
 * @param {{ creatorProratedBranch: boolean }} branch
 * @returns {{ creditRounded: number, callInMinutes: number, callInSec: number }}
 */
const computeSubscriberDeductCredits = (pricing, durationSec, { creatorProratedBranch }) => {
  const callInMinutes = Math.floor(durationSec / 60);
  const callInSec = Math.floor(durationSec % 60);
  const proratedCredits = roundCredits(durationSec * (pricing.ratePerMinuteCredits / 60));

  let creditNum;
  if (pricing.pricingModel === 'per_minute') {
    const minimumCredits =
      pricing.minDurationMins > 0
        ? roundCredits(pricing.minDurationMins * pricing.ratePerMinuteCredits)
        : 0;
    creditNum = creatorProratedBranch
      ? proratedCredits
      : Math.max(proratedCredits, minimumCredits);
  } else if (creatorProratedBranch) {
    creditNum = proratedCredits;
  } else {
    const blocks = getFlatRateBlocksCharged(durationSec, pricing.minDurationMins);
    const blockCredits =
      pricing.flatRateCredits ??
      pricing.sessionCostCredits ??
      roundCredits(pricing.ratePerMinuteCredits * pricing.minDurationMins);
    creditNum = roundCredits(blocks * blockCredits);
  }

  const creditRounded = Math.max(0, roundCredits(creditNum));
  return { creditRounded, callInMinutes, callInSec };
};

/**
 * Never charge more than the subscriber's wallet holds (e.g. flat-rate first block > balance).
 * @param {number} credits — computed bill from {@link computeSubscriberDeductCredits}
 * @param {number} [walletCredits] — subscriber wallet credits at deduct time
 * @returns {number}
 */
export const capSubscriberDeductCreditsToWalletBalance = (credits, walletCredits) => {
  const billed = Math.max(0, roundCredits(credits));
  if (!Number.isFinite(walletCredits) || walletCredits < 0) return billed;
  const wallet = Math.max(0, roundCredits(walletCredits));
  return Math.max(0, roundCredits(Math.min(billed, wallet)));
};

/**
 * Build POST body for wallet/deduct-call-credit (subscriber after a call with duration greater than zero).
 * Rates come from getCallRequestPricingDetails (USD → credits via ×2).
 *
 * **Flat-rate (e.g. $100 / 5 min):** Effective rate = session $ ÷ block minutes (×2 credits/min).
 * Subscriber hangup → session blocks charged at sessionCostCredits per block.
 * Creator termination (`remote`), low balance, or network/system end → prorated seconds at derived credits/min.
 *
 * **Per-minute:** bill actual seconds at the per-minute rate. When `minDurationMins` is set and
 * the subscriber ends the call early, charge at least the minimum duration.
 *
 * @param {string|null|undefined} creatorId
 * @param {object|null|undefined} creatorInfo
 * @param {'voice'|'video'} callKind
 * @param {number} durationSec
 * @param {{ hangupParty?: string|null, endedReason?: string|null, subscriberWalletCredits?: number }} [endMeta]
 * @returns {object|null}
 */
export const buildDeductCallCreditPayload = (
  creatorId,
  creatorInfo,
  callKind,
  durationSec,
  endMeta = {}
) => {
  const id = creatorId != null && creatorId !== '' ? String(creatorId) : '';
  if (!id || !Number.isFinite(durationSec) || durationSec <= 0) return null;

  const pricing = getCallRequestPricingDetails(creatorInfo, callKind);
  if (!pricing.ok) return null;

  const hangupParty = endMeta?.hangupParty ?? null;
  const endedReason = endMeta?.endedReason ?? null;
  const remoteCreatorTermination = isRemoteCreatorTermination(hangupParty, endedReason);
  const actualTimeOnlyTermination = isActualTimeOnlyBillingTermination(endedReason);
  // Local user_hangup: subscriber ended the call. Per-minute calls still use actual seconds,
  // but enforce any configured minimum duration.
  const subscriberExplicitHangup =
    hangupParty === 'local' && String(endedReason || '').toLowerCase() === 'user_hangup';
  const creatorProratedBranch =
    (remoteCreatorTermination || actualTimeOnlyTermination) && !subscriberExplicitHangup;

  const { creditRounded, callInMinutes, callInSec } = computeSubscriberDeductCredits(
    pricing,
    durationSec,
    { creatorProratedBranch }
  );
  const creditToCharge = capSubscriberDeductCreditsToWalletBalance(
    creditRounded,
    endMeta?.subscriberWalletCredits
  );
  if (creditToCharge <= 0) return null;

  return {
    creatorId: id,
    credit: String(creditToCharge),
    callInMinutes,
    callInSec,
    callMediaType: normalizeCallKind(callKind) === 'video' ? 'video' : 'audio',
  };
};
